import React, { useState } from "react"

import dayjs from "dayjs"
import { useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"
import Table from "~tailwindui/Table/Table"

import LocalTime from "components/LocalTime"
import DataDateRange from "components/OrganizationAdmin/components/DataDateRange"
import Header from "components/OrganizationAdmin/components/DisplayableFormikForm/Header"
import { sessionPresenceIcon } from "components/OrganizationAdmin/components/presenceIcon"

import Tabs from "./Tabs"

const SessionsPage = () => {
  const history = useHistory()

  const defaultStartingAt = dayjs().startOf("year")
  const defaultEndingAt = dayjs()
  const [startingAt, setStartingAt] = useState(defaultStartingAt)
  const [endingAt, setEndingAt] = useState(defaultEndingAt)

  const tabs = ["Past", "Future", "All"]
  const [selected, setSelected] = useState("Past")
  const [page, setPage] = useState(1)
  const [result] = useQuery({
    query: sessionsQuery,
    variables: {
      page,
      future: selected === "Future",
      past: selected === "Past",
      startingAt,
      endingAt,
    },
  })

  return (
    <>
      <Header title="Sessions" />
      <DataDateRange
        startDate={startingAt}
        setStartDate={setStartingAt}
        endDate={endingAt}
        setEndDate={setEndingAt}
      />
      <WithLoadingIndicator result={result}>
        {({ data: { viewer } }) => (
          <>
            <Table
              title={
                <>
                  <Tabs
                    tabs={tabs}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </>
              }
              data={viewer.organization.airtutorsSessions}
              changePage={setPage}
              collapsible={false}
              rowCallback={session => history.push(`/sessions/${session.id}`)}
              cols={[
                { header: "Subject", value: session => session.subject.name },
                {
                  header: "Date",
                  value: session => (
                    <LocalTime key="startsAt" timestamp={session.startsAt} />
                  ),
                },
                {
                  header: "Students",
                  value: session => (
                    <ul key="studentsList" className="list-none">
                      {session.students.map(student => (
                        <li key={student.id}>{student?.fullName}</li>
                      ))}
                    </ul>
                  ),
                },
                { header: "Tutor", value: session => session.tutor?.fullName },
                {
                  header: "Present?",
                  value: session => sessionPresenceIcon(session),
                },
                {
                  header: "Status",
                  value: session => (
                    <span
                      key="status"
                      className={
                        ["canceled", "declined"].includes(session.statusState)
                          ? "text-red-500"
                          : ""
                      }
                    >
                      {session.statusState}
                    </span>
                  ),
                },
                {
                  hidden: !viewer.canOrganizationAdminAccessFinances,
                  header: "Billed?",
                  value: session => session.billState,
                },
              ]}
            />
          </>
        )}
      </WithLoadingIndicator>
    </>
  )
}

const sessionFields = gql`
  fragment SessionFields on AirtutorsSession {
    id
    startsAt
    endsAt
    noShow
    statusState
    billState
    formattedActualDuration
    tutor {
      id
      fullName
    }
    students {
      id
      fullName
    }
    subject {
      id
      name
    }
  }
`

const sessionsQuery = gql`
  ${sessionFields}
  query sessions(
    $page: Int!
    $future: Boolean
    $past: Boolean
    $startingAt: DateTime!
    $endingAt: DateTime!
  ) {
    viewer {
      canOrganizationAdminAccessFinances
      organization {
        airtutorsSessions(
          page: $page
          future: $future
          past: $past
          startDate: $startingAt
          endDate: $endingAt
        ) {
          currentPage
          totalPages
          data {
            ...SessionFields
          }
        }
      }
    }
  }
`

export default SessionsPage
